import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CustomerNotification, CustomerNotificationCounts } from './customer-notification.model';
import { CustomerNotificationCategory, AckStatus } from '../shared/enums/smarthub-enums';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Moment } from 'moment';
import urlJoin from 'url-join';
import { PayrollCycleType, PayrollTypeInfo } from './payroll-types.service';
import * as moment from 'moment-timezone';
import { FeatureToggleService } from '../shared/services/feature-toggle/feature-toggle.service';
import { AcknowledgmentFilter } from '../components/alerts/details/alerts-filters.model';
import { AlertNotificationDetails } from './alert-notification-details.model';
import { ROUTE_TOKEN_GATEWAY_URL } from '../shared/interceptors/gateway-url.interceptor';

const EST_NAME = 'America/New_York';
const DATE_ONLY_FORMAT = 'YYYY-MM-DD';

@Injectable()
export class CustomerNotificationService {

  constructor(
    private productConfigService: FeatureToggleService,
    private http: HttpClient,
  ) { }

  getRecentCountsAllCategories(tenantId: string): Observable<CustomerNotificationCounts[]> {
    let params = (new HttpParams());
    if (this.productConfigService.isFeatureEnabled('enableFifteenDayAlertsAndNotifications')) {
      const toAckDate = moment.tz(EST_NAME);
      const fromAckDate = toAckDate.clone().utc().subtract(15, 'days').tz(EST_NAME);
      params = params
        .append('fromAckDateTime', fromAckDate.format(DATE_ONLY_FORMAT))
        .append('toAckDateTime', toAckDate.format(DATE_ONLY_FORMAT));
    }
    return this.http.get<CustomerNotificationCounts[]>(
      urlJoin(
        this._getUri(tenantId, params, 'customernotifications', 'counts')
      ),
      { headers: {} }
    );
  }

  getRecentCounts(
    tenantId: string,
    category: CustomerNotificationCategory,
    ackFilter?: AcknowledgmentFilter): Observable<CustomerNotificationCounts> {
    const categoryOrdinal = category.toString();
    let params = (new HttpParams()).append('category', categoryOrdinal);
    if (this.productConfigService.isFeatureEnabled('enableFifteenDayAlertsAndNotifications')) {
      if (ackFilter) {
        params = params
          .append('fromAckDateTime', ackFilter.fromAckDateTime.format(DATE_ONLY_FORMAT))
          .append('toAckDateTime', ackFilter.toAckDateTime.format(DATE_ONLY_FORMAT));
      } else {
        const toAckDate = moment.tz(EST_NAME);
        const fromAckDate = toAckDate.clone().utc().subtract(15, 'days').tz(EST_NAME);
        params = params
          .append('fromAckDateTime', fromAckDate.format(DATE_ONLY_FORMAT))
          .append('toAckDateTime', toAckDate.format(DATE_ONLY_FORMAT));
      }
    }
    return this.http.get<CustomerNotificationCounts>(
        urlJoin(
          this._getUri(tenantId, params, 'customernotifications', 'count')
        ),
        { headers: {} }
      );
  }

  getCustomerNotifications(
    tenantId: string,
    category: CustomerNotificationCategory,
    ackFilter?: AcknowledgmentFilter,
    payUtcDate?: Moment,
    payGroups?: string[],
    hasReturnedFunds?: boolean,
    hasNotReturnedFunds?: boolean,
    pType?: PayrollCycleType,
    companyId?: string
    ): Observable<CustomerNotification[]> {

    if ((hasReturnedFunds === false && hasNotReturnedFunds === false) || ackFilter.includeAck === undefined) {
      return of([]); // (X && !X) requested... so return nothing.
    }

    const categoryOrdinal = category.toString();
    const ackStatusOrdinal = AckStatus[ackFilter.includeAck].toString();
    let params = new HttpParams()
      .append('category', categoryOrdinal);
    // Acknowledgment filter
    params = this.productConfigService.isFeatureEnabled('enableFifteenDayAlertsAndNotifications')
      ? params
        .append('includeAck', ackStatusOrdinal)
        .append('fromAckDateTime', ackFilter.fromAckDateTime.format(DATE_ONLY_FORMAT))
        .append('toAckDateTime', ackFilter.toAckDateTime.format(DATE_ONLY_FORMAT))
      : params
        .append('sinceHoursElapsed', String(24));
    // Page params
    params = params
      .append('page', String(1))
      .append('per_page', String(500));
    params = payUtcDate ? params.append('payUtcDate', payUtcDate.format(DATE_ONLY_FORMAT)) : params;
    params = payGroups ?  params.append('payGroups', payGroups.join()) : params;
    if (hasReturnedFunds !== hasNotReturnedFunds) { // XOR only query by hasReturnedFunds if one is true and the other is false.
      params = params.append('hasReturnedFunds', hasReturnedFunds ? 'true' : 'false');
    } else if (!hasReturnedFunds) { // if they are false filter only by unknown types.
      params = params.append('types', '0');
    }
    params = pType ?      params.append('payrollTypes', new PayrollTypeInfo(pType).paySequences.join()) : params;
    params = companyId ?  params.append('companyId', companyId) : params;
    return this.http.get<CustomerNotification[]>(
      urlJoin(
        this._getUri(tenantId, params, 'customernotifications', 'recent')
      ),
      { headers: {} }
    );
  }

  updateAcknowledged(tenantId: string, id: string, flag: boolean): Observable<CustomerNotification> {
    return this.http.put<CustomerNotification>(
      this._getUri(tenantId, null, 'customernotifications', id, 'acknowledged'),
      { acknowledged: flag },
    );
  }

  getNotificationDetails(tenantId: string, id: string): Observable<AlertNotificationDetails> {
    return this.http.get<AlertNotificationDetails>(
      urlJoin(this._getBaseUri(), `cui-payment/v1/tenants/${encodeURI(tenantId)}/alertandnotificationdetail/${encodeURI(id)}`)
    );
  }

  _getBaseUri(): string {
    return ROUTE_TOKEN_GATEWAY_URL;
  }

  _getUri(tenantId: string, params: HttpParams, ...pathSegments: string[]) {
    const baseUrl = this._getBaseUri();
    let uri = urlJoin(baseUrl, `payment/v1/tenants/${encodeURI(tenantId)}`, ... pathSegments);
    if (params) {
      uri += '?' + params.toString();
    }
    return uri;
  }

  getNotificationDetailsForExport(tenantId: string, ids: string[]): Observable<AlertNotificationDetails[]> {
    return this.http.post<AlertNotificationDetails[]>(
      urlJoin(this._getBaseUri(), `cui-payment/v1/tenants/${encodeURI(tenantId)}/alertandnotificationdetails`),
      ids
    );
  }
}
